import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import toast, { Toaster } from 'react-hot-toast';
import axios from 'axios';

import Navigation from './navigation';
import { io } from 'socket.io-client';
import { CALL_RESET, ONLINE_USERS, USER_LOGOUT } from './redux/constants/userConstants';
import { getAllNotificationsAction } from './redux/actions/chatActions';
import IncomingCallModal from './screens/VideoCallScreen/IncomingCallModal';

import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import { getOrgUserCount, getClientLogoAction, getLoggedinUserAction } from './redux/actions/userActions';
import SuccessMessageToast from './components/Toast/SuccessMessageToast';
import ErrorBoundary from './components/ErrorBoundary';
import i18n from './i18n';

// P2 — endpoints where a 401 means "those credentials are wrong", not
// "your session ended". Sending an invalid password used to trip the
// global session-expiry handler below: the user was told their session had
// expired instead of that the password was wrong, and — worse — a failed
// login attempt cleared localStorage and logged them out of any other tab
// where they were legitimately signed in.
//
// The login screen already renders the backend's own message
// (`Incorrect Email or Password.`), so all this has to do is stay out of
// the way. Matched on the path, so a query string or absolute base URL
// can't slip past it.
const AUTH_ATTEMPT_PATHS = [
  '/users/login',
  '/users/signup',
  '/users/forgotPassword',
  '/users/resetPassword',
  '/users/verify-totp',
  '/users/login-totp',
];

export function isAuthAttempt(config) {
  const url = String(config?.url || '');
  if (!url) return false;
  const path = url.split('?')[0].replace(/\/+$/, '');
  return AUTH_ATTEMPT_PATHS.some(p => path.endsWith(p));
}

const App = () => {
  const dispatch = useDispatch();
  const [socket, setSocket] = React.useState(null);
  const [callModal, setCallModal] = React.useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const userLogin = useSelector(state => state.userLogin);
  const loggedinUser = useSelector(state => state.loggedinUser);

  const callReducer = useSelector(state => state.callReducer);

  // Hard-refresh hydration guard.
  //
  // On page reload the store rehydrates `userLogin.userInfo` from
  // localStorage (login response) but NOT `state.loggedinUser` or
  // `state.tenantConfig`. Historically AppLayout was the only mount
  // that fetched those — but every route wraps its screen in
  // <RequireFeature>, which holds a blank placeholder until
  // tenantConfig resolves. Because ClaimsScreen (and its wrapping
  // AppLayout) never mount until RequireFeature releases, and
  // RequireFeature never releases until tenantConfig fetches, and
  // tenantConfig never fetches until AppLayout mounts — the whole
  // route stayed as an empty placeholder div forever. The user saw
  // it as "blank page after refresh".
  //
  // Fire getLoggedinUserAction() from App.js (which mounts
  // unconditionally, above the Router) whenever we have a hydrated
  // userLogin but no loggedinUser yet. getLoggedinUserAction
  // internally also dispatches fetchTenantConfig, so a single call
  // unblocks both.
  React.useEffect(() => {
    const hydratedUser =
      userLogin?.userInfo?.data?.user || userLogin?.userInfo?.data?.data;
    const alreadyFetched = Boolean(loggedinUser?.userInfo);
    if (hydratedUser && !alreadyFetched && !loggedinUser?.loading) {
      dispatch(getLoggedinUserAction());
    }
  }, [
    userLogin?.userInfo?.data?.user,
    userLogin?.userInfo?.data?.data,
    loggedinUser?.userInfo,
    loggedinUser?.loading,
    dispatch,
  ]);

  // LOCALE RESOLUTION LIVES IN ONE PLACE, AND IT IS NOT HERE.
  //
  // This component used to resolve currency / date format / timezone /
  // region from tenantConfig + user preferences and write the window
  // globals the formatters read. LocaleContext held a SECOND, different
  // answer at the same time — seeded from localStorage over a hardcoded
  // Swiss default — and nothing reconciled them, so `useLocale().currency`
  // said CHF for every tenant while formatMoney correctly said BHD.
  //
  // utils/LocaleContext now owns the whole thing: resolveLocale() is the
  // one resolver and LocaleProvider is the one writer of window.REGURE_*.
  // It reads the same two redux slices this effect read, so nothing about
  // WHEN the resolution happens has changed — only how many answers exist.

  const setupSocket = React.useCallback(
    id => {
      if (id && !socket) {
        console.log('🔌 Socket URL:', process.env.REACT_APP_BASE_SOCKET_URL);
        const newSocket = io.connect(process.env.REACT_APP_BASE_SOCKET_URL, {
          withCredentials: true,
          transports: ['websocket', 'polling'],
          query: {
            id,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        });

        newSocket.on('disconnect', () => {
          setSocket(null);
          setTimeout(() => setupSocket(id), 3000);
        });

        newSocket.on('connect', () => {});

        setSocket(newSocket);
      }
    },
    [socket],
  );

  // Global 401/402 handler
  React.useEffect(() => {
    let loggingOut = false;
    const interceptorId = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401 && !isAuthAttempt(error.config)) {
          if (!loggingOut) {
            loggingOut = true;
            console.error('🚨 401 LOGOUT TRIGGERED', {
              url: error.config?.url,
              method: error.config?.method,
              status: error.response?.status,
              message: error.response?.data?.message,
              timestamp: new Date().toISOString(),
            });
            localStorage.removeItem('userInfo');
            dispatch({ type: USER_LOGOUT });
            toast.error(
              i18n.t('common.errors.sessionExpired', { defaultValue: 'Your session has expired. Please sign in again.' }),
              { toastId: 'session-expired', duration: 5000 },
            );
          }
        } else if (error.response?.status === 402) {
          // Trial expired — show message and redirect to onboarding to subscribe
          toast.error('Your free trial has expired. Please subscribe to continue.', { toastId: 'trial-expired', autoClose: 8000 });
          setTimeout(() => {
            window.location.hash = '/orgs/onboard?reason=trial_expired';
          }, 1500);
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, [dispatch]);

  React.useEffect(() => {
    if (callReducer.status && callReducer.status === 'Incoming') {
      setCallModal(true);
    } else {
      setCallModal(false);
    }
    if (callReducer.status && callReducer.status === 'Decline') {
      dispatch({ type: CALL_RESET });
      toast.error(`Call declined!`, {
        style: {
          borderRadius: '10px',
          background: 'red',
          color: '#fff',
        },
      });
    }
  }, [callReducer, dispatch]);

  React.useEffect(() => {
    if (socket) {
      const handleEndCallNotAccepted = () => {
        if (callReducer.status && callReducer.status === 'Incoming') {
          dispatch({ type: CALL_RESET });
        }
        setCallModal(false);
      };
      socket.on('endCallNotAccepted', handleEndCallNotAccepted);

      return () => {
        socket.off('endCallNotAccepted', handleEndCallNotAccepted);
      };
    }
  }, [socket, callReducer, dispatch]);

  React.useEffect(() => {
    // Connect socket for any authenticated user.
    // Legacy: modules.chat flag. Regure: modules.messaging flag (or no flag gate).
    const legacyUser = userLogin?.userInfo?.data?.user;
    const regureUser = userLogin?.userInfo?.data?.data;
    const userId = legacyUser?._id || regureUser?._id;
    console.log('🔐 User ID:', userId, 'Socket exists:', !!socket);
    if (userId) {
      setupSocket(userId);
    }
  }, [setupSocket, userLogin]);

  React.useEffect(() => {
    const getOrgTotalUsers = async clientId => {
      dispatch(getOrgUserCount(clientId));
    };
    if (userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user.role) {
      if (userLogin.userInfo.data.user.role === 'fileAdmin') {
        getOrgTotalUsers(userLogin.userInfo.data.user.org);
      } else if (userLogin.userInfo.data.user.role === 'org') {
        getOrgTotalUsers(userLogin.userInfo.data.user._id);
      }
    }
  }, [userLogin?.userInfo?.data?.user?.role, dispatch]);

  React.useEffect(() => {
    if (!(userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user._id) && socket) {
      socket.disconnect();
    }
  }, [socket, userLogin]);

  React.useEffect(() => {
    if (userLogin && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user._id) {
      dispatch(getAllNotificationsAction('get', '', 1));
      dispatch(getClientLogoAction());
    }
  }, [dispatch, userLogin]);

  React.useEffect(() => {
    if (socket) {
      const handleOnlineUsers = user => {
        dispatch({ type: ONLINE_USERS, payload: user.onlineUsers });
      };

      const handleNewIncMessage = message => {
        if (window.location.hash !== '#/chat' && message && message.message && message.message.senderName) {
          setToastMessage(`${message.message.senderName} has sent you a new message.`);
          setShowSuccessToast(true);
        }
      };

      const handleNotificationRefresh = () => {
        dispatch(getAllNotificationsAction('get', '', 1));
      };

      socket.on('online-users', handleOnlineUsers);
      socket.on('newIncMessage', handleNewIncMessage);
      socket.on('sendRequest', handleNotificationRefresh);
      socket.on('GroupRequestSendResponse', handleNotificationRefresh);
      socket.on('AcceptGroupRequestResponse', handleNotificationRefresh);
      socket.on('acceptRequest', handleNotificationRefresh);
      socket.on('removeRequest', handleNotificationRefresh);
      socket.on('cancelRequest', handleNotificationRefresh);

      return () => {
        socket.off('online-users', handleOnlineUsers);
        socket.off('newIncMessage', handleNewIncMessage);
        socket.off('sendRequest', handleNotificationRefresh);
        socket.off('GroupRequestSendResponse', handleNotificationRefresh);
        socket.off('AcceptGroupRequestResponse', handleNotificationRefresh);
        socket.off('acceptRequest', handleNotificationRefresh);
        socket.off('removeRequest', handleNotificationRefresh);
        socket.off('cancelRequest', handleNotificationRefresh);
      };
    }
  }, [socket, dispatch, userLogin]);

  return (
    <ErrorBoundary>
      <Fragment>
        <Navigation userLogin={userLogin} socket={socket} setSocket={setSocket} />
        <Toaster position='bottom-center' reverseOrder={false} />
        <ToastContainer position='bottom-right' />
        <IncomingCallModal
          callModal={callModal}
          onDecline={() => {
            if (socket) {
              socket.emit('callDecline', {
                id: callReducer && callReducer.data ? callReducer.data.socketId : '',
              });
            }
          }}
        />
        <SuccessMessageToast message={toastMessage} showToast={showSuccessToast} onClose={() => setShowSuccessToast(false)} duration={6500} />
      </Fragment>
    </ErrorBoundary>
  );
};

export default App;
